/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {useLingui} from '@lingui/react/macro';
import {useMemo} from 'react';

export interface SplashQuote {
	readonly text: string;
	readonly source: string;
}

export function useSplashQuotes(): ReadonlyArray<SplashQuote> {
	const {t} = useLingui();

	return useMemo(() => {
		const quotes: Array<SplashQuote> = [
			{text: t`Aren't you supposed to be sleeping?`, source: 'kitkatz'},
			{text: t`After careful consideration, we have decided we did, in fact, need loading lines.`, source: 'vky'},
			{text: t`There is no cloud... only other people's computers.`, source: 'Siliconic'},
			{text: t`Did you know that Fluxer spelled backwards makes no sense? Why would you do that?`, source: 'jackzie'},
			{text: t`Have you considered touching grass while you wait?`, source: 'Lilith'},
			{
				text: t`Unfortunately, it doesn't run on regular unleaded gasoline. It requires something with a little more kick. Plutonium.`,
				source: 'Cookie',
			},
			{text: t`9/10 dentists recommend Fluxer.`, source: 'Hugopuntocom'},
			{text: t`Is this thing on?`, source: 'Pufty'},
			{text: t`Now supports sending messages over the Internet!`, source: '99'},
			{text: t`Flux capacitors use 99.99% less water than your typical AI data centre!`, source: 'snappyapple632'},
			{text: t`No soldering skills required!`, source: '99'},
			{text: t`Removed Herobrine.`, source: 'Manfre'},
			{text: t`Please enjoy this loading quote while you wait.`, source: 'illspirit'},
			{
				text: t`Fluxer is 100% powered by flux capacitors. And furries. We don't forget about the furries.`,
				source: 'jb',
			},
			{text: t`Unable to decrypt loading quote.`, source: 'SteveLinkNoah'},
			{text: t`Remember to hydrate! Or don't, I'm not the boss of you.`, source: 'jb'},
			{text: t`Congratulations! This is the special rare loading screen message!`, source: 'jb'},
			{text: 'Nu \u00e4r det dags f\u00f6r en fika.', source: 'Chip'},
			{text: t`Is Fluxer loading? Why are you asking me? I'm just a loading screen!`, source: 'Caus'},
			{text: t`Welcome back to 2015!`, source: 'Pan'},
			{text: t`Connecting to irc.fluxer.com:6667...`, source: 'viriona'},
			{text: t`Wait, hold on, I'm not ready yet!`, source: 'Vee'},
			{text: t`Feel that? That's Fluxer.`, source: 'TheFastestBoy'},
			{text: t`The real fluxer was the friends we made along the way.`, source: 'viriona'},
			{text: t`Is Fluxer an instrument? No, Fluxer is a communication platform.`, source: 'Comfy_Deer'},
			{text: t`Hi! I'm a loading screen!`, source: 'jackzie'},
			{text: t`3 Billion Devices Run Fluxer.`, source: 'jackzie'},
			{text: t`To connect or not to connect, that is the question.`, source: 'Tea'},
			{text: t`Please do not the fluxer.`, source: 'Chris'},
			{text: t`I am the one who fluxes.`, source: 'Anonymous'},
			{text: t`Installing Fluxer on the nearest smart fridge...`, source: 'TyphonBagrid'},
			{text: t`Ya like Jazz?`, source: 'catnaros'},
			{text: t`If it smells like chicken, you're holding the soldering iron wrong.`, source: 'viriona'},
			{text: t`Everything is in flux.`, source: 'Alyx'},
			{text: t`Happy new year 1999!`, source: '99'},
			{text: t`Heating up the engine.`, source: 'Ray'},
			{text: t`Pong!`, source: 'Cookie'},
			{text: t`Everyday I'm fluxin' it.`, source: 'Liminalis'},
		];
		quotes.push({
			text: t`Your odds of landing on this particular loading quote were 1 in ${String(quotes.length + 1)}.`,
			source: 'vky',
		});
		return quotes;
	}, [t]);
}
